import { motion } from "framer-motion";
import { HandHeart } from "lucide-react";
import { LQWorship } from "@/hooks/useLaylatulQadr";
import { ProgressRing } from "@/components/ui/ProgressRing";

interface WorshipTrackerProps {
  nightNumber: number;
  worship?: LQWorship;
  onSave: (nightNumber: number, data: Partial<LQWorship>) => Promise<void>;
}

const worshipItems = [
  { key: "extra_salah" as const, label: "Extra Salah", emoji: "🕌" },
  { key: "quran_recited" as const, label: "Quran Recitation", emoji: "📖" },
  { key: "dhikr_done" as const, label: "Dhikr", emoji: "📿" },
  { key: "charity_given" as const, label: "Charity", emoji: "🤲" },
  { key: "dua_made" as const, label: "Dua", emoji: "💛" },
];

export const WorshipTracker = ({ nightNumber, worship, onSave }: WorshipTrackerProps) => {
  const completed = worshipItems.filter(item => worship?.[item.key]).length;
  const progress = (completed / worshipItems.length) * 100;

  const toggle = (key: keyof LQWorship) => {
    onSave(nightNumber, { [key]: !worship?.[key] });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}
      className="space-y-4"
    >
      <h2 className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-3">
        <HandHeart className="w-4 h-4" />
        Worship Momentum
      </h2>

      <div className="bg-[#0f0f23]/50 rounded-sm p-6 border border-gold/10">
        <div className="flex items-center gap-6 mb-6">
          <ProgressRing
            progress={progress}
            size={80}
            strokeWidth={5}
            color="hsl(45, 70%, 50%)"
            bgColor="rgba(226, 177, 91, 0.1)"
          >
            <span className="text-lg font-display text-gold">{completed}</span>
            <span className="text-[10px] text-cream/40">of {worshipItems.length}</span>
          </ProgressRing>
          <div className="flex-1">
            <p className="text-cream/70 text-sm">
              {completed === 0 && "Begin your night of worship"}
              {completed > 0 && completed < worshipItems.length && "Keep going — every act is multiplied"}
              {completed === worshipItems.length && "SubhanAllah — a blessed night of worship"}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          {worshipItems.map((item) => {
            const isActive = worship?.[item.key] || false;
            return (
              <button
                key={item.key}
                onClick={() => toggle(item.key as keyof LQWorship)}
                className={`w-full flex items-center gap-4 p-3 rounded-sm transition-all text-left ${
                  isActive
                    ? "bg-gold/10 border border-gold/25"
                    : "border border-transparent hover:bg-gold/5"
                }`}
              >
                <span className="text-base">{item.emoji}</span>
                <span className={`text-sm flex-1 ${isActive ? "text-cream" : "text-cream/50"}`}>
                  {item.label}
                </span>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  isActive ? "border-gold bg-gold/20" : "border-cream/20"
                }`}>
                  {isActive && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-2 h-2 rounded-full bg-gold"
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};
